import { request } from './api-client';
import { emailKey } from './email-api';
export const integrationKey = () => [...emailKey(), 'integrations'];
export interface MeetingPayload {
  title: string;
  invitees: string[];
  start: string;
  end: string;
  timezone: string;
  location: string;
  description: string;
}
export interface CalendarEvent {
  id: string;
  startsAt: string;
  endsAt: string;
  revision: number;
  payload: {
    summary?: string;
    description?: string;
    location?: string;
    htmlLink?: string;
    hangoutLink?: string;
    start?: { date?: string; dateTime?: string; timeZone?: string };
    end?: { date?: string; dateTime?: string };
    organizer?: { email?: string };
    attendees?: Array<{
      email?: string;
      self?: boolean;
      responseStatus?: string;
    }>;
  };
}
export interface ScheduleAttempt {
  id: string;
  status: string;
  receipt?: { payload?: MeetingPayload; htmlLink?: string } | null;
  lastError?: string | null;
}
export interface CalendarDraft {
  id: string;
  revision: number;
  payload: MeetingPayload;
  status: string;
  accountEmail: string;
  attempt?: ScheduleAttempt | null;
}
export interface IntegrationSummary {
  state: string;
  totalCount?: number;
  todayCount?: number;
  readyDraftCount?: number;
  complete?: boolean;
  lastSyncedAt?: string | null;
  lastError?: string | null;
  canConnect?: boolean;
  eligibleAddressCount?: number;
  setupIssue?: string | { message: string } | null;
  activeRun?: { id: string; status: string } | null;
  connections?: Array<{ id: string; accountEmail: string; status: string }>;
  nextMeeting?: CalendarEvent | null;
}
export interface HomeIntegrations {
  preferences: {
    homeIntroSeenAt: string | null;
    timezone: string;
    timezoneSaved: boolean;
  };
  range: { from: string; to: string; timezone: string };
  asOf: string;
  recomputeAt?: string;
  calendar: IntegrationSummary;
  gmail: IntegrationSummary;
}
export interface EventPage {
  items: CalendarEvent[];
  totalCount: number;
  nextCursor: string | null;
  complete: boolean;
  timezone: string;
}
export interface ResultPage {
  id: string;
  provider: 'gmail' | 'google_calendar';
  title: string;
  createdAt: string;
  revision: number;
  totalCount: number;
  items: Array<{ id: string; available: boolean }>;
  nextCursor: string | null;
  scanStatus?: string | null;
}
export const calendarApi = {
  home: () => request<HomeIntegrations>('/v1/home/integrations'),
  preferences: (body: { introSeen?: boolean; timezone?: string }) =>
    request<HomeIntegrations['preferences']>('/v1/home/preferences', {
      method: 'PATCH',
      body,
    }),
  connect: () =>
    request<{ authorizationUrl: string }>('/v1/calendar/oauth/start', {
      method: 'POST',
      body: { returnUrl: `${window.location.origin}/app/brief` },
    }),
  disconnect: (id: string) =>
    request(`/v1/calendar/connections/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  sync: (id: string) =>
    request<{ syncRunId: string }>(
      `/v1/calendar/connections/${encodeURIComponent(id)}/sync`,
      { method: 'POST', body: {} },
    ),
  run: (id: string) =>
    request<{ status: string }>(
      `/v1/calendar/sync-runs/${encodeURIComponent(id)}`,
    ),
  events: (cursor?: string) =>
    request<EventPage>(
      `/v1/calendar/events${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  event: (id: string) =>
    request<CalendarEvent>(`/v1/calendar/events/${encodeURIComponent(id)}`),
  drafts: () => request<{ items: CalendarDraft[] }>('/v1/calendar/drafts'),
  draft: (id: string) =>
    request<CalendarDraft>(`/v1/calendar/drafts/${encodeURIComponent(id)}`),
  edit: (id: string, revision: number, payload: MeetingPayload) =>
    request(`/v1/calendar/drafts/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { expectedRevision: revision, payload },
    }),
  dismiss: (id: string) =>
    request(`/v1/calendar/drafts/${encodeURIComponent(id)}/dismiss`, {
      method: 'POST',
      body: {},
    }),
  schedule: (id: string, expectedRevision: number, idempotencyKey: string) =>
    request<ScheduleAttempt>(
      `/v1/calendar/drafts/${encodeURIComponent(id)}/schedule`,
      { method: 'POST', body: { expectedRevision, idempotencyKey } },
    ),
  reconcile: (id: string) =>
    request<ScheduleAttempt>(
      `/v1/calendar/attempts/${encodeURIComponent(id)}/reconcile`,
      { method: 'POST', body: {} },
    ),
  results: (id: string, cursor?: string) =>
    request<ResultPage>(
      `/v1/integration-results/${encodeURIComponent(id)}${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  refreshResults: (id: string) =>
    request(`/v1/integration-results/${encodeURIComponent(id)}/refresh`, {
      method: 'POST',
      body: {},
    }),
};
export function homeQuery() {
  return {
    queryKey: integrationKey(),
    queryFn: calendarApi.home,
    refetchInterval: (q: { state: { data: HomeIntegrations | undefined } }) => {
      const data = q.state.data;
      if(!data)return 3000;
      const times = [
        data?.recomputeAt,
        data?.calendar.nextMeeting?.startsAt,
        data?.calendar.nextMeeting?.endsAt,
      ]
        .filter((t): t is string => !!t)
        .map(Date.parse)
        .filter((t) => t > Date.now());
      return Math.max(
        100,
        Math.min(30_000, ...times.map((t) => t - Date.now() + 100)),
      );
    },
    retry: false,
  };
}
